import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import {Button} from "@/components/ui/button";

type StaffMember = {
  id: string;
  fullname: string;
  active: boolean;
};

type StaffAbsence = {
  id: string;
  staffId: string;
  staffFullname: string;
  absenceDate: string;
  partOfDay: "full_day" | "morning" | "afternoon";
  leaveType: "paid" | "unpaid" | "special";
};

interface StaffAbsenceDayMenuProps {
  day: Date;
  top: number;
  left: number;
  staff: StaffMember[];
  absences: StaffAbsence[];
  isSaving: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onSave: (input: {
    staffId: string;
    absenceDate: string;
    partOfDay: "full_day" | "morning" | "afternoon";
    leaveType: "paid" | "unpaid" | "special";
  }) => void;
  onDelete: (absenceId: string) => void;
}

const partOptions = [
  { value: "full_day", label: "Journée" },
  { value: "morning", label: "Matin" },
  { value: "afternoon", label: "Après-midi" },
] as const;

const leaveTypeOptions = [
  { value: "paid", label: "Congé payé" },
  { value: "special", label: "Congé spécial" },
  { value: "unpaid", label: "Congé sans solde" },
] as const;

function isPartOfDay(value: string): value is (typeof partOptions)[number]["value"] {
  return partOptions.some((option) => option.value === value);
}

function isLeaveType(value: string): value is (typeof leaveTypeOptions)[number]["value"] {
  return leaveTypeOptions.some((option) => option.value === value);
}

export default function StaffAbsenceDayMenu(props: StaffAbsenceDayMenuProps) {
  const isoDay = DateTime.fromJSDate(props.day).toISODate() || "";
  const [staffId, setStaffId] = useState(props.staff[0]?.id || "");
  const [partOfDay, setPartOfDay] = useState<(typeof partOptions)[number]["value"]>("full_day");
  const [leaveType, setLeaveType] = useState<(typeof leaveTypeOptions)[number]["value"]>("paid");

  const dayLabel = DateTime.fromJSDate(props.day).toFormat("dd/MM/yyyy");

  const sortedAbsences = useMemo(() => {
    return [...props.absences].sort((a, b) => {
      if (a.staffFullname !== b.staffFullname) {
        return a.staffFullname.localeCompare(b.staffFullname);
      }
      return a.partOfDay.localeCompare(b.partOfDay);
    });
  }, [props.absences]);

  const handleSave = () => {
    if (!staffId) {
      toast.error("Choisissez un membre du personnel.");
      return;
    }

    props.onSave({
      staffId,
      absenceDate: isoDay,
      partOfDay,
      leaveType,
    });
  };

  return (
    <div
      id="StaffAbsenceDayMenu"
      className="fixed z-30 w-[360px] max-w-[calc(100vw-24px)] rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_28px_80px_rgba(15,23,42,0.18)]"
      style={{ top: props.top, left: Math.max(12, props.left - 140) }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-lg font-semibold text-slate-900">Absence du personnel</div>
          <div className="mt-1 text-sm text-slate-500">{dayLabel}</div>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={props.onClose}
        >
          Fermer
        </Button>
      </div>

      <div className="mt-5 space-y-4">
        <label className="block">
          <div className="mb-2 text-sm font-semibold text-slate-900">Membre du personnel</div>
          <select
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base"
            value={staffId}
            onChange={(event) => setStaffId(event.target.value)}
          >
            {props.staff.map((member) => (
              <option key={member.id} value={member.id}>
                {member.fullname}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-slate-900">Durée</div>
            <select
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base"
              value={partOfDay}
              onChange={(event) => {
                const value = event.target.value;
                if (isPartOfDay(value)) {
                  setPartOfDay(value);
                }
              }}
            >
              {partOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <div className="mb-2 text-sm font-semibold text-slate-900">Type</div>
            <select
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base"
              value={leaveType}
              onChange={(event) => {
                const value = event.target.value;
                if (isLeaveType(value)) {
                  setLeaveType(value);
                }
              }}
            >
              {leaveTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <Button
          onClick={handleSave}
          disabled={props.isSaving || props.staff.length === 0}
        >
          {props.isSaving ? "Enregistrement..." : "Ajouter l'absence"}
        </Button>
      </div>

      <div className="mt-6">
        <div className="text-sm font-semibold text-slate-900">Absences déjà saisies</div>
        {sortedAbsences.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            Aucune absence pour ce jour.
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {sortedAbsences.map((absence) => {
              const partLabel =
                absence.partOfDay === "full_day"
                  ? "Journée"
                  : absence.partOfDay === "morning"
                    ? "Matin"
                    : "Après-midi";
              const leaveLabel =
                absence.leaveType === "paid"
                  ? "Congé payé"
                  : absence.leaveType === "special"
                    ? "Congé spécial"
                    : "Congé sans solde";

              return (
                <div
                  key={absence.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-3"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {absence.staffFullname}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {partLabel} • {leaveLabel}
                    </div>
                  </div>
                  <Button
                    onClick={() => props.onDelete(absence.id)}
                    disabled={props.isDeleting}
                    variant="destructive"
                    size="sm"
                  >
                    {props.isDeleting ? "Suppression..." : "Supprimer"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
